import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const FooterWrapper = styled.footer`
  background: var(--background-dark);
  color: var(--accent-color);
  padding: 60px 0 20px;
  margin-top: auto;
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-bottom: 40px;
`;

const FooterSection = styled.div`
  h3 {
    color: var(--secondary-color);
    font-size: 1.25rem;
    margin-bottom: 20px;
    font-family: "Inter", sans-serif;
  }

  p,
  li {
    color: #b8c5d1;
    line-height: 1.6;
    margin-bottom: 8px;
    font-family: "Inter", sans-serif;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  a {
    color: #b8c5d1;
    text-decoration: none;
    transition: color 0.3s ease;
    font-family: "Inter", sans-serif;

    &:hover {
      color: var(--secondary-color);
    }
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  width: 490px;
  gap: 10px;
  margin-bottom: 15px;

  i {
    color: var(--secondary-color);
    width: 20px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--secondary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white !important;
  text-decoration: none;
  transition: all 0.3s ease;

  i {
    color: white !important;
    font-size: 1rem;
  }

  &:hover {
    background: #c9a660;
    transform: translateY(-2px);

    i {
      color: white !important;
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid #2c3e50;
  padding-top: 20px;
  text-align: center;
  color: #e5e7eb;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 20px;

  a {
    color: white;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: var(--secondary-color);
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const Footer = () => {
  return (
    <FooterWrapper>
      <FooterContainer>
        <FooterContent>
          {/* Company Info */}
          <FooterSection>
            <h3>Baypoint Storage</h3>
            <p>
              Premium shipping containers for all your storage, construction,
              and business needs. Quality containers delivered nationwide with
              exceptional service.
            </p>
            <SocialLinks>
              <SocialLink
                href="https://facebook.com/baypointstorage"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <i className="fab fa-facebook-f"></i>
              </SocialLink>
              <SocialLink
                href="https://twitter.com/baypointstorage"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
              >
                <i className="fab fa-twitter"></i>
              </SocialLink>
              <SocialLink
                href="https://linkedin.com/company/baypointstorage"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <i className="fab fa-linkedin-in"></i>
              </SocialLink>
              <SocialLink
                href="https://instagram.com/baypointstorage"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <i className="fab fa-instagram"></i>
              </SocialLink>
            </SocialLinks>
          </FooterSection>

          {/* Quick Links */}
          <FooterSection>
            <h3>Quick Links</h3>
            <ul>
              <li>
                <Link to="/about">About Us</Link>
              </li>
              <li>
                <Link to="/containers">Our Containers</Link>
              </li>
              <li>
                <Link to="/services">Services</Link>
              </li>
              <li>
                <Link to="/services/new-containers">New Containers</Link>
              </li>
              <li>
                <Link to="/services/used-containers">Used Containers</Link>
              </li>
              <li>
                <Link to="/services/custom-modifications">
                  Custom Modifications
                </Link>
              </li>
              <li>
                <Link to="/track">Track Shipment</Link>
              </li>
            </ul>
          </FooterSection>

          {/* Services */}
          <FooterSection>
            <h3>Services</h3>
            <ul>
              <li>
                <Link to="/services/mobile-offices">Mobile Offices</Link>
              </li>
              <li>
                <Link to="/services/nationwide-delivery">
                  Nationwide Delivery
                </Link>
              </li>
              <li>
                <Link to="/services/container-storage-solutions">
                  Storage Solutions
                </Link>
              </li>
              <li>
                <Link to="/contact">Request Quote</Link>
              </li>
              <li>
                <Link to="/faq">FAQ</Link>
              </li>
            </ul>
          </FooterSection>

          {/* Contact Info */}
          <FooterSection>
            <h3>Contact Info</h3>
            <ContactInfo>
              <i className="fas fa-map-marker-alt"></i>
              <div>
                <p>
                  <strong>Address</strong>
                </p>

                <p>6401 Geil Ln, Louisville, KY 40219</p>
              </div>
            </ContactInfo>

            <ContactInfo>
              <i className="fas fa-handshake"></i>
              <div>
                <p>
                  <strong>Regional Partners</strong>
                </p>
                <p>23631 US-270, Woodward, OK 73801</p>
                <p>3260 5th S, Salt Lake City, UT 84104</p>
              </div>
            </ContactInfo>
            <ContactInfo>
              <i className="fas fa-phone"></i>
              <div>
                <p>
                  <a href="tel:+14845429847">+1 (484) 542-9847</a>
                </p>
              </div>
            </ContactInfo>
            <ContactInfo>
              <i className="fas fa-envelope"></i>
              <div>
                <p>
                  <a href="mailto:sales@baypointstorage.com">
                    sales@baypointstorage.com
                  </a>
                </p>
              </div>
            </ContactInfo>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <FooterLinks>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-conditions">Terms & Conditions</Link>
            <Link to="/return-refund-policy">Return & Refund Policy</Link>
          </FooterLinks>
          <p>
            © 2026 Baypoint Storage. All rights reserved. | Website by Baypoint
            Storage Team
          </p>
        </FooterBottom>
      </FooterContainer>
    </FooterWrapper>
  );
};

export default Footer;
